import React, { useEffect, useState, useCallback } from 'react';
import LoadingSpinner from '../shared/LoadingSpinner';
import { TabContent, DetailPanel, ListDetailLayout, ListPanel, EditActionsBar, FileLink } from '../../pages/admin/AdminPage.styles';
import { EditableInput } from '../product/EditableFields.styles';
import { useEditMode } from '../../context/EditModeContext';
import { useAuthStore } from '../../context/AuthContext';
import useUpdateCoatingManufacturer from '../../api/useUpdateCoatingManufacturer';
import { CoatingManufacturer } from '../../api/useGetCoatingManufacturers';
import { CoatingNote } from '../../api/useGetCoatingNotes';
import { styled } from '@linaria/react';

const NotesSection = styled.div`
    margin-top: 1rem;

    .notes-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .no-notes {
        color: #6c757d;
        font-style: italic;
    }
`;

interface CoatingManufacturerTabProps {
    manufacturers: CoatingManufacturer[];
    loading: boolean;
    error: string | null;
    selectedManufacturer: CoatingManufacturer | null;
    setSelectedManufacturer: (manufacturer: CoatingManufacturer | null) => void;
    coatingNotes?: CoatingNote[];
    onNavigateToNote?: (note: CoatingNote) => void;
    onRefresh?: () => void;
}

const CoatingManufacturerTab: React.FC<CoatingManufacturerTabProps> = ({
    manufacturers,
    loading,
    error,
    selectedManufacturer,
    setSelectedManufacturer,
    coatingNotes = [],
    onNavigateToNote,
    onRefresh,
}) => {
    const { updateCoatingManufacturer, isLoading: isUpdating } = useUpdateCoatingManufacturer();
    const { isInEditMode, setEditMode } = useEditMode();
    const { loggedInUser } = useAuthStore();
    const [formData, setFormData] = useState<CoatingManufacturer | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);

    const canEdit = loggedInUser !== null;

    // Initialize form data when manufacturer is selected
    useEffect(() => {
        if (selectedManufacturer) {
            setFormData(selectedManufacturer);
            // Don't reset edit mode if this is a new item (id === 0)
            if (selectedManufacturer.id !== 0) {
                setEditMode(false);
            }
        }
    }, [selectedManufacturer, setEditMode]);

    // Handle edit mode
    const handleEdit = useCallback(() => {
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode]);

    // Handle add new coating manufacturer
    const handleAddNew = useCallback(() => {
        const newManufacturer: CoatingManufacturer = {
            id: 0,
            name: '',
        };
        setSelectedManufacturer(newManufacturer);
        setEditMode(true);
        setUpdateError(null);
    }, [setEditMode, setSelectedManufacturer]);

    // Handle cancel
    const handleCancel = useCallback(() => {
        setFormData(selectedManufacturer);
        setEditMode(false);
        setUpdateError(null);
    }, [selectedManufacturer, setEditMode]);

    // Handle save
    const handleSave = useCallback(async () => {
        if (!formData) return;

        try {
            await updateCoatingManufacturer(formData);
            setEditMode(false);
            setUpdateError(null);
            // Refresh the list to show updated data
            onRefresh?.();
        } catch (err: any) {
            setUpdateError(err.message);
        }
    }, [formData, updateCoatingManufacturer, setEditMode, onRefresh]);

    // Handle field changes
    const handleFieldChange = useCallback((field: keyof CoatingManufacturer, value: any) => {
        setFormData((prev) => {
            if (!prev) return prev;
            return {
                ...prev,
                [field]: value,
            };
        });
    }, []);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!manufacturers.length) return;

            const currentIndex = manufacturers.findIndex((m) => m.id === selectedManufacturer?.id);

            switch (event.key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    const nextIndex = currentIndex < manufacturers.length - 1 ? currentIndex + 1 : 0;
                    setSelectedManufacturer(manufacturers[nextIndex]);
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    const prevIndex = currentIndex > 0 ? currentIndex - 1 : manufacturers.length - 1;
                    setSelectedManufacturer(manufacturers[prevIndex]);
                    break;
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [manufacturers, selectedManufacturer, setSelectedManufacturer]);

    return (
        <TabContent>
            <ListDetailLayout>
                <ListPanel>
                    {loading && <LoadingSpinner text="Loading coating manufacturers..." />}

                    {error && (
                        <div className="error-state">
                            <i className="fas fa-exclamation-triangle"></i> Error loading coating manufacturers: {error}
                        </div>
                    )}

                    {!loading && !error && manufacturers.length === 0 && (
                        <div className="empty-state">
                            <i className="fas fa-industry"></i>
                            <p>No coating manufacturers found</p>
                        </div>
                    )}

                    {!loading && manufacturers.length > 0 && (
                        <div className="table-container">
                            <table className="data-table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {manufacturers.map((manufacturer) => (
                                        <tr
                                            key={manufacturer.id}
                                            className={`table-row ${selectedManufacturer?.id === manufacturer.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedManufacturer(manufacturer)}
                                        >
                                            <td className="name-cell">{manufacturer.name}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </ListPanel>

                <DetailPanel>
                    {selectedManufacturer && formData ? (
                        <>
                            {isInEditMode && (
                                <EditActionsBar>
                                    <button onClick={handleAddNew} className="btn-add-new" title="Add new coating manufacturer">
                                        <i className="fas fa-plus"></i> New Coating Manufacturer
                                    </button>
                                    <div>
                                        <button onClick={handleSave} className="btn-save" disabled={isUpdating} title="Save changes">
                                            <i className="fas fa-save"></i> {isUpdating ? 'Saving...' : 'Save'}
                                        </button>
                                        <button onClick={handleCancel} className="btn-cancel" disabled={isUpdating} title="Cancel changes">
                                            <i className="fas fa-times"></i> Cancel
                                        </button>
                                    </div>
                                </EditActionsBar>
                            )}
                            <div className="detail-content">
                                <div className="detail-header">
                                    <h3>{formData.name || 'New Manufacturer'}</h3>
                                    <div>
                                        <div className="header-actions">
                                            {canEdit && !isInEditMode && (
                                                <button onClick={handleEdit} className="edit-btn" title="Edit">
                                                    <i className="fas fa-pencil-alt"></i>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {updateError && (
                                    <div className="error-alert">
                                        <i className="fas fa-exclamation-triangle"></i> {updateError}
                                    </div>
                                )}
                                <div className="detail-body">
                                    <div className="detail-section">
                                        <h4>Basic Information</h4>
                                        <div className="detail-grid">
                                            <div className="detail-item">
                                                <label>Name</label>
                                                {isInEditMode ? (
                                                    <EditableInput
                                                        value={formData.name || ''}
                                                        onChange={(e) => handleFieldChange('name', e.target.value)}
                                                        placeholder="Enter manufacturer name"
                                                    />
                                                ) : (
                                                    <span>{formData.name}</span>
                                                )}
                                            </div>
                                            <div className="detail-item">
                                                <label>External Id</label>
                                                <span>{formData.id}</span>
                                            </div>
                                        </div>
                                    </div>
                                    {formData.id !== 0 && (
                                        <div className="detail-section">
                                            <h4>Manufacturer Notes</h4>
                                            <NotesSection>
                                                {(() => {
                                                    const manufacturerNotes = coatingNotes.filter((note) => note.manufacturerId === formData.id);
                                                    const generalNotes = manufacturerNotes.filter((note) => note.isGeneralNote);
                                                    const specificNotes = manufacturerNotes.filter((note) => !note.isGeneralNote);

                                                    if (manufacturerNotes.length === 0) {
                                                        return <span className="no-notes">No notes for this manufacturer</span>;
                                                    }

                                                    return (
                                                        <div className="notes-list">
                                                            {generalNotes.map((note) => (
                                                                <FileLink
                                                                    key={note.id}
                                                                    onClick={() => onNavigateToNote?.(note)}
                                                                    title="View General Note"
                                                                >
                                                                    <i className="fas fa-file-alt"></i>
                                                                    General Note
                                                                </FileLink>
                                                            ))}
                                                            {specificNotes.map((note) => (
                                                                <FileLink
                                                                    key={note.id}
                                                                    onClick={() => onNavigateToNote?.(note)}
                                                                    title={`View Specific Note ${note.number}`}
                                                                >
                                                                    <i className="fas fa-sticky-note"></i>
                                                                    Specific Note {note.number}
                                                                </FileLink>
                                                            ))}
                                                        </div>
                                                    );
                                                })()}
                                            </NotesSection>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="detail-placeholder">
                            <i className="fas fa-industry"></i>
                            <h3>Select a coating manufacturer</h3>
                            <p>Choose a coating manufacturer from the list to view its details</p>
                        </div>
                    )}
                </DetailPanel>
            </ListDetailLayout>
        </TabContent>
    );
};

export default CoatingManufacturerTab;
